import React, { FunctionComponent } from "react";
import { CellMeasurerCacheParams } from "react-virtualized";

import { DEFAULT_FORM_ITEM_HEIGHT } from "../../constants";
import { LOC_KEY_QUICK_ACCESS_MENU_HISTORY_ITEMS } from "../../locKeys";
import { logger } from "../../logger";
import { searchGroupToNavigationSection } from "../../navigation";
import { SearchResult, SearchResultType } from "../../search";
import { GroupHeader } from "../GroupHeader";
import { MenuItem } from "../MenuItem";
import { MenuVirtualizedList, MenuVirtualizedListItemRenderer, MenuVirtualizedListKeyGenerator } from "../MenuVirtualizedList";
import { OpenedItem } from "../OpenedItem";
import { ProfileItem } from "../ProfileItem";
import { QuickAccessGroupHeaderLayout } from "../QuickAccessGroupHeaderLayout";
import { QuickAccessMenuHeader } from "../QuickAccessMenuHeader";

export type QuickAccessSearchResultsComponentProps = {
    /** Výsledky vyhledávání */
    searchResults: ReadonlyArray<SearchResult>;
    /** Index zaměřené položky ve výsledcích vyhledání. */
    focusedItemIndex: number | undefined;
};

const cellMeasureCacheParams: CellMeasurerCacheParams = {
    defaultHeight: DEFAULT_FORM_ITEM_HEIGHT,
    minHeight: DEFAULT_FORM_ITEM_HEIGHT,
    fixedWidth: true,
};

export const QuickAccessSearchResultsComponent: FunctionComponent<QuickAccessSearchResultsComponentProps> = ({ searchResults, focusedItemIndex }) => (
    <MenuVirtualizedList
        stickyHeaderContent={<QuickAccessMenuHeader />}
        items={searchResults}
        {...{ itemRenderer, keyGenerator, cellMeasureCacheParams, focusedItemIndex }}
    />
);

const keyGenerator: MenuVirtualizedListKeyGenerator<SearchResult> = ({ id, type, searchGroup }) => `${searchGroup}-${type}-${id}`;

const itemRenderer: MenuVirtualizedListItemRenderer<SearchResult> = ({ id, type, searchGroup }) => {
    const openActionEnabled = searchGroup !== LOC_KEY_QUICK_ACCESS_MENU_HISTORY_ITEMS;
    const section = searchGroupToNavigationSection(searchGroup);
    if (type === SearchResultType.groupHeader) {
        return (
            <QuickAccessGroupHeaderLayout>
                <GroupHeader locKey={id as string}/>
            </QuickAccessGroupHeaderLayout>
        );
    } else if (type === SearchResultType.formInstance) {
        return (
            <OpenedItem formInstanceId={id as string} section={section} qa={searchGroup} />
        );
    } else if (type === SearchResultType.form) {
        return (
            <MenuItem formItemId={id as number} qa={searchGroup} {...{ openActionEnabled, section }} />
        );
    } else if (type === SearchResultType.profile) {
        return (
            <ProfileItem profileId={id as number} qa={searchGroup} {...{ openActionEnabled, section }} />
        );
    } else {
        logger.error("Cannot render search result with id %s because it has unknown type: %s", id, type);
        return <div>Unknown item: {type} {id}</div>;
    }
};
